import { createFileRoute, Link } from "@tanstack/react-router";
import type { PhuketAreaPage } from "@/lib/phuket-area-pages";
import { ProvincePageShell } from "@/components/ProvincePageShell";
import { pageHeadForProvince } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildAreasItemListSchema, buildSpeakableSchema } from "@/lib/schema";
import { getActiveProvince } from "@/provinces/_registry";

export const Route = createFileRoute("/areas/")({
  loader: async (): Promise<{ areas: PhuketAreaPage[] }> => {
    const { PHUKET_AREA_PAGES } = await import("@/lib/phuket-area-pages");
    return { areas: PHUKET_AREA_PAGES };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const areas = loaderData?.areas ?? [];
    const base = pageHeadForProvince({
      title: `พื้นที่บริการทั่ว${config.nameTh} — ${areas.length || 40} โซน | Thai Notary ${config.nameEn}`,
      description: `บริการ Notary Public, แปลรับรอง และวีซ่าครอบคลุมทุกหาด มารีน่า ย่านธุรกิจและสนามบินใน${config.nameTh} — เลือกโซนใกล้คุณ พร้อมทีมลงพื้นที่ on-site`,
      path: "/areas",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/areas")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "พื้นที่บริการ", url: "/areas" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildAreasItemListSchema(
              areas.map((a) => ({
                slug: a.slug,
                name: `${a.nameTh} (${a.nameEn})`,
                description: a.intro?.slice(0, 140),
              })),
            ),
          ),
        },
      ],
    };
  },
  component: AreasIndex,
});

function AreasIndex() {
  const { config } = getActiveProvince();
  const { areas: PHUKET_AREA_PAGES } = Route.useLoaderData() as { areas: PhuketAreaPage[] };
  const grouped = {
    beach: PHUKET_AREA_PAGES.filter((a) => a.type === "beach"),
    marina: PHUKET_AREA_PAGES.filter((a) => a.type === "marina"),
    business: PHUKET_AREA_PAGES.filter((a) => a.type === "business" || a.type === "old-town"),
  };
  const labels = { beach: "หาดท่องเที่ยว", marina: "มารีน่า & ท่าเทียบเรือ", business: "ย่านธุรกิจ & เมืองเก่า" };
  return (
    <ProvincePageShell
      eyebrow="Service Coverage"
      title={`พื้นที่บริการทั่ว${config.nameTh}`}
      subtitle={`เลือกพื้นที่ของคุณเพื่อดูบริการ Notary, แปลและวีซ่าที่ให้บริการ on-site — ครอบคลุม ${config.districtCount} อำเภอใน${config.nameTh}`}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "พื้นที่บริการ" }]}
    >
      <div className="mx-auto max-w-7xl space-y-12 px-4 py-16">
        {(Object.keys(grouped) as Array<keyof typeof grouped>).map((k) => (
          <section key={k}>
            <h2 className="text-2xl font-black text-primary">{labels[k]}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {grouped[k].map((a) => (
                <Link key={a.slug} to="/areas/$area" params={{ area: a.slug }} className="group rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gold">{a.districtTh}</div>
                  <h3 className="mt-1 text-lg font-extrabold text-primary group-hover:text-gold">{a.nameTh} — {a.nameEn}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground line-clamp-3">{a.intro}</p>
                  <div className="mt-3 text-[11px] font-bold text-gold">ค่าเดินทาง: {a.travelFee}</div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </ProvincePageShell>
  );
}